/**
 * MtoSpecsPage.js
 *
 * Authenticated portal page linked from the "Send to Purchasing" email. Purchasers
 * open /mto-specs?estimate=<n>&rev=<r>, which lists each part in the requisition with
 * its current spec/datasheet PDF and a download button (auth blob fetch — plain links
 * can't carry the bearer token). Specs are versioned per part in the Part Rate DB.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const MtoSpecsPage = () => {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const estimate = params.get('estimate') || '';
  const jobNumber = params.get('job') || params.get('job_number') || '';
  const rev = params.get('rev') || '';
  const { viewers, isConnected, connectionState } = usePresence('mtoSpecs', estimate || jobNumber || 'global', true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [zipBusy, setZipBusy] = useState(false);

  const fetchManifest = useCallback(async () => {
    if (!estimate && !jobNumber) { setError('Missing estimate or job in the link.'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const qp = [];
      if (estimate) qp.push(`estimate_number=${encodeURIComponent(estimate)}`);
      if (jobNumber) qp.push(`job_number=${encodeURIComponent(jobNumber)}`);
      if (rev) qp.push(`rev=${encodeURIComponent(rev)}`);
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/specs-manifest?${qp.join('&')}`);
      if (resp.data?.success) setRows(resp.data.data || []);
      else setError('Failed to load specs list.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load specs list.');
    } finally { setLoading(false); }
  }, [instance, accounts, estimate, jobNumber, rev]);

  useEffect(() => { fetchManifest(); }, [fetchManifest]);

  const download = async (row) => {
    if (!row.datasheet_id) return;
    setBusyId(row.datasheet_id);
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/datasheet/${row.datasheet_id}/file`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.original_filename || `${row.part_number || 'spec'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('Failed to download spec. You may need to log in.');
    } finally { setBusyId(null); }
  };

  const downloadAll = async () => {
    setZipBusy(true);
    try {
      const qp = [];
      if (estimate) qp.push(`estimate_number=${encodeURIComponent(estimate)}`);
      if (jobNumber) qp.push(`job_number=${encodeURIComponent(jobNumber)}`);
      if (rev) qp.push(`rev=${encodeURIComponent(rev)}`);
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/specs-archive?${qp.join('&')}`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(estimate || jobNumber || 'specs').replace(/[^\w.-]+/g, '_')}_specs.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('Failed to download specs archive. You may need to log in.');
    } finally { setZipBusy(false); }
  };

  const withSpecs = rows.filter((r) => r.datasheet_id);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem' }}>
      <h2 style={{ margin: '0 0 0.35rem', color: '#111827' }}>📄 MTO Specs / Datasheets</h2>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', margin: '0 0 1rem', flexWrap: 'wrap' }}>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
          {estimate ? <>Estimate <strong>{estimate}</strong></> : <>Job <strong>{jobNumber}</strong></>}
          {rev ? <> — Rev {rev}</> : ''} · {withSpecs.length} spec file(s)
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          {withSpecs.length > 0 && (
            <button onClick={downloadAll} disabled={zipBusy}
              style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: zipBusy ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
              {zipBusy ? 'Preparing ZIP…' : `⬇ Download all (${withSpecs.length}) as ZIP`}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
      ) : error ? (
        <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c' }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No parts found for this selection.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <thead>
            <tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
              <th style={{ padding: '8px 10px' }}>Part #</th>
              <th style={{ padding: '8px 10px' }}>Description</th>
              <th style={{ padding: '8px 10px' }}>Eng Code</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Qty</th>
              <th style={{ padding: '8px 10px' }}>Spec / Datasheet</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.part_number} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{r.part_number}</td>
                <td style={{ padding: '7px 10px' }}>{r.part_name || r.description || '—'}</td>
                <td style={{ padding: '7px 10px' }}>{r.engineering_code || '—'}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>{Number(r.qty_total || 0).toLocaleString()}</td>
                <td style={{ padding: '7px 10px' }}>
                  {r.datasheet_id ? (
                    <button onClick={() => download(r)} disabled={busyId === r.datasheet_id}
                      style={{ background: '#0d6efd', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: busyId === r.datasheet_id ? 'not-allowed' : 'pointer', fontSize: '0.78rem' }}>
                      {busyId === r.datasheet_id ? 'Downloading…' : `⬇ ${r.original_filename || 'Download'}`}
                    </button>
                  ) : (
                    <span style={{ color: '#cbd5e1' }}>No spec attached</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MtoSpecsPage;
